import * as d3 from "d3";
import * as _ from "underscore";
import Config from "../config";
import { catMaybes } from "../lib/array.extended";
import { clone } from "../lib/clone";
import { confidenceInterval } from "../lib/confidenceInterval";
import { memoize } from "../lib/memoize";
import * as Dims from "./dimension";
import {
  AllDimensionValues,
  DimensionValues,
  DimKey,
  mkAllDimensionValues,
  mkDimensionValues,
  summaryKey,
  Weights,
} from "./dimension";
import { RegionId } from "./region";

export type OrgId = string;
export type HeadquartersId = string;
export type SectorId = string;
export type IndustryId = string;

export type Year = string;

// -----------------------------------------------------------------------------
// Survey Data CSV
export interface ResponseR {
  organization_id: OrgId;

  /* Year when the data was collected. */
  year: Year;

  /* The region where the respondent is from. */
  region_id: RegionId;

  // Core dimensions
  ethics: number;
  social_cohesion: number;
  benefit: number;
  quality_of_life: number;
}

// -----------------------------------------------------------------------------
// Organization Data CSV
export interface OrganizationR {
  id: OrgId;
  name: string;
  legal_name: string;
  canton: string;
  website: string;

  headquarters_id: HeadquartersId;
  headquarters_city: string;
  headquarters_street: string;
  headquarters_country: any;
  headquarters_adjective_m_singular: string;
  headquarters_adjective_m_plural: string;
  headquarters_adjective_f_singular: string;
  headquarters_adjective_f_plural: string;
  headquarters_adjective_mixed_singular: string;
  headquarters_adjective_mixed_plural: string;

  sector_id: SectorId;
  sector_name: string;
  sector_article: string;
  sector_plural: string;
  // FIXME: These are not set in DE, so they are likely not used
  sector_number?: string;
  sector_gender?: string;

  industry_id: IndustryId;
  industry_name: string;
  industry_article: string;
  industry_plural: string;
  // FIXME: These are not set in DE, so they are likely not used
  industry_number?: string;
  industry_gender?: string;
}

// TODO-MAKE-ENUM
export type OrgRating =
  | 3 // top
  | 2 // middle
  | 1; // low

// -----------------------------------------------------------------------------
// Organization Attributes

export interface Industry {
  key: IndustryId;
  name: string;

  article: string;
  plural: string;
  number: string;
  gender: string;
}

export interface Sector {
  key: SectorId;
  name: string;

  article: string;
  plural: string;
  number: string;
  gender: string;
}

export interface Headquarters {
  key: HeadquartersId;
  name: string;

  adjective: string;
  adjective_m_singular: string;
  adjective_m_plural: string;
  adjective_f_singular: string;
  adjective_f_plural: string;
  adjective_mixed_singular: string;
  adjective_mixed_plural: string;
  country: string;
}

export interface Delta {
  value: number;
  reference: undefined | Year;
}

// -----------------------------------------------------------------------------
// Dimensions

export const averageForDimension = (org: Organization, dimKey: DimKey): undefined | number => {
  const dv = org.dimensions.dimValues(dimKey);
  return dv ? dv.avg : undefined;
};

export interface ValueDevelopmentItem {
  year: Year;
  value: number;
}

export const dimValueDevelopment = (org: Organization, dimKey: DimKey): ValueDevelopmentItem[] => {
  const ret = catMaybes(
    org.availableYears.map((year) => {
      const value = averageForDimension(clone(org, { year }), dimKey);
      return value !== undefined ? { year, value } : undefined;
    }),
  );

  return _.sortBy(ret, (x) => +x.year);
};

// -----------------------------------------------------------------------------
// Organization class used throughout the application

export interface Organization {
  readonly orgResponses: ResponseR[];
  // ^ Responses for this organization.

  readonly responses: ResponseR[];
  // ^ Responses for the year+region

  readonly id: OrgId;

  readonly year: Year;
  readonly regions: RegionId[];
  // ^ The year+regions define which responses we consider for the derived data.

  readonly weights: Weights;
  // ^ This is used to compute the summary dimension from the core dimensions.

  globalConfidenceInterval(): [number, number];
  readonly selectedDimension: null | DimKey;

  readonly name: string;
  readonly legal_name: string;
  readonly website: string;

  readonly headquarters_city: string;
  readonly headquarters_street: string;

  readonly headquarter_object: Headquarters;
  readonly industry_object: Industry;
  readonly sector_object: Sector;

  readonly availableYears: Year[]; // NonEmpty
  // ^ Sorted ascending.

  readonly dimensions: AllDimensionValues;

  readonly comparisonYear: undefined | Year;
  readonly comparisonDimensions: AllDimensionValues;
  // ^ If 'comparisonYear' is undefined, this is an empty array.

  readonly rating: OrgRating;

  readonly delta: { [dimKey: string]: Delta };
  // ^ Delta to 'comparisonYear' of all dimensions.
}

interface OrganizationImpl extends Organization {
  _responses: typeof _responses;
  _rating: typeof _rating;
  _dimensions: typeof _dimensions;
  coreDimensions: DimensionValues[];
  _coreDimensions: typeof _coreDimensions;
  _availableYears: typeof _availableYears;
  _comparisonYear: typeof _comparisonYear;
  summaryDimension: DimensionValues;
  _summaryDimension: typeof _summaryDimension;
  _comparisonDimensions: typeof _comparisonDimensions;
  _comparisonResponses: typeof _comparisonResponses;
  _delta: typeof _delta;

  comparisonResponses: ResponseR[];
}

const _responses = (orgResponses: ResponseR[], year: Year, regionIds: RegionId[]): ResponseR[] =>
  orgResponses.filter((x) => x.year === year && (regionIds.length === 0 || regionIds.indexOf(x.region_id) !== -1));

const _rating = (
  dimensions: AllDimensionValues,
  [globalAvgLow, globalAvgHigh]: [number, number],
  dimKey: DimKey,
): OrgRating => {
  const dv = dimensions.dimValues(dimKey);
  if (!dv) {
    return 1;
  }
  const [low, high] = dv.ci;
  const grp = low > globalAvgHigh ? "top" : high >= globalAvgLow ? "middle" : "low";
  const rg = _.find(Config.ratingGroups, (d) => d.name === grp);
  return rg ? rg.rating : 1;
};

const _dimensions = (summaryDimension: DimensionValues, coreDimensions: DimensionValues[]): AllDimensionValues =>
  mkAllDimensionValues([summaryDimension].concat(coreDimensions));

const _coreDimensions = (responses: ResponseR[]): AllDimensionValues => {
  return Dims.coreDimensions({ js: true }).map((dimension: any): DimensionValues => {
    const key: DimKey = dimension.key;
    const values = responses.map((d) => +(d as any)[key]);
    const mean = d3.mean(values);
    return mkDimensionValues(
      key,
      () => values,
      () => Config.averageMeasure(values),
      () => confidenceInterval(values, mean),
    );
  });
};

const _summaryDimension = (coreDimensions: DimensionValues[], weights: Weights): DimensionValues => {
  const dimWeights = coreDimensions.map((d) => Dims.weightForKey(weights, d.key));

  // Some speedup could be achieved by unrolling the inner loop (reduce+f)
  const f =
    (i: number) =>
    (a: number, dv: DimensionValues, j: number): number =>
      a + dv.values[i] * dimWeights[j];
  const values = coreDimensions[0].values.map((_unused, i) => coreDimensions.reduce(f(i), 0));

  const avg = Config.averageMeasure(values);
  const mean = Config.averageMeasure === d3.mean ? avg : d3.mean(values);

  return mkDimensionValues(
    summaryKey,
    () => values,
    () => avg,
    () => confidenceInterval(values, mean),
  );
};

const _availableYears = (orgResponses: ResponseR[]): Year[] => {
  const s = new Set();
  orgResponses.forEach((x) => s.add(x.year));
  return Array.from(s.values()).sort((a, b) => +(a as string) - +(b as string)) as Year[];
};

const _comparisonYear = (availableYears: Year[], selectedYear: Year): undefined | Year => {
  const index = availableYears.indexOf(selectedYear);
  if (index === 0) {
    return undefined;
  } else {
    return availableYears[index - 1];
  }
};

const _comparisonResponses = (
  allResponses: ResponseR[],
  year: undefined | Year,
  regionIds: RegionId[],
): ResponseR[] => {
  if (year === undefined) {
    return [];
  } else {
    return allResponses.filter(
      (x) => x.year === year && (regionIds.length === 0 || regionIds.indexOf(x.region_id) !== -1),
    );
  }
};

const _comparisonDimensions = (responses: ResponseR[], weights: Weights): AllDimensionValues => {
  if (responses.length === 0) {
    return mkAllDimensionValues([]);
  }

  const coreDimensions = _coreDimensions(responses);
  return mkAllDimensionValues([_summaryDimension(coreDimensions, weights)].concat(coreDimensions));
};

const _delta = (
  dimensions: AllDimensionValues,
  comparisonYear: undefined | Year,
  comparisonDimensions: AllDimensionValues,
): { [dimKey: string]: Delta } => {
  const ret: { [dimKey: string]: Delta } = {};
  if (comparisonYear) {
    dimensions.forEach((d) => {
      const c = comparisonDimensions.dimValues(d.key);
      if (c) {
        ret[d.key] = { value: (100 / c.avg) * d.avg - 100, reference: comparisonYear };
      }
    });
  }
  return ret;
};

export const mkOrganization = (
  org: OrganizationR,
  orgResponses: ResponseR[],
  headquarter_object: Headquarters,
  industry_object: Industry,
  sector_object: Sector,
): Organization =>
  ({
    orgResponses,

    year: "1000" as Year,
    regions: [],
    weights: Dims.weights(Dims.initial()),
    globalConfidenceInterval: () => [0, 0],
    selectedDimension: null,

    name: org.name,
    legal_name: org.legal_name,
    website: org.website,
    headquarters_city: org.headquarters_city,
    headquarters_street: org.headquarters_street,
    headquarter_object,
    industry_object,
    sector_object,

    get id(): OrgId {
      return org.id;
    },

    get responses(): ResponseR[] {
      return this._responses(this.orgResponses, this.year, this.regions);
    },
    _responses: memoize(_responses),

    get rating(): OrgRating {
      return this._rating(this.dimensions, this.globalConfidenceInterval(), this.selectedDimension || summaryKey);
    },
    _rating: memoize(_rating),

    get dimensions(): AllDimensionValues {
      return this._dimensions(this.summaryDimension, this.coreDimensions);
    },
    _dimensions: memoize(_dimensions),

    get coreDimensions(): AllDimensionValues {
      return this._coreDimensions(this.responses);
    },
    _coreDimensions: memoize(_coreDimensions),

    get summaryDimension(): DimensionValues {
      return this._summaryDimension(this.coreDimensions, this.weights);
    },
    _summaryDimension: memoize(_summaryDimension),

    get availableYears(): Year[] {
      return this._availableYears(this.orgResponses);
    },
    _availableYears: memoize(_availableYears),

    get comparisonYear(): undefined | Year {
      return this._comparisonYear(this.availableYears, this.year);
    },
    _comparisonYear: memoize(_comparisonYear),

    get comparisonResponses(): ResponseR[] {
      return this._comparisonResponses(this.orgResponses, this.comparisonYear, this.regions);
    },
    _comparisonResponses: memoize(_comparisonResponses),

    get comparisonDimensions(): AllDimensionValues {
      return this._comparisonDimensions(this.comparisonResponses, this.weights);
    },
    _comparisonDimensions: memoize(_comparisonDimensions),

    get delta(): { [dimKey: string]: Delta } {
      return this._delta(this.dimensions, this.comparisonYear, this.comparisonDimensions);
    },
    _delta: memoize(_delta),
  }) as OrganizationImpl;

// -----------------------------------------------------------------------------
// Functions

export const sortOrganizations = (orgs: Organization[], dimKey: DimKey): Organization[] =>
  orgs.slice(0).sort((a, b) => (averageForDimension(b, dimKey) || 0) - (averageForDimension(a, dimKey) || 0));

export const medianAverageForOrgs = (orgs: Organization[], dimKey: DimKey): number =>
  d3.median(catMaybes(orgs.map((org) => averageForDimension(org, dimKey))));

export function linkToOrganization(locale: string, org: Pick<Organization, "id">) {
  return `/${locale}/atlas/${org.id}`;
}

export function findPrecedingOrganization(all: Array<Pick<Organization, "id">>, org: Pick<Organization, "id">) {
  const idx = all.findIndex((x) => org.id === x.id);
  return idx != -1 && idx > 0 ? all[idx - 1] : null;
}

export function findSucceedingOrganization(all: Array<Pick<Organization, "id">>, org: Pick<Organization, "id">) {
  const idx = all.findIndex((x) => org.id === x.id);
  return idx != -1 && idx < all.length - 1 ? all[idx + 1] : null;
}
